import { Box, Button, Modal as VHSModal } from '@wistia/vhs';
import type { JSX } from 'react';
import { cloneElement, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import type { ReadonlyTuple } from 'type-fest/source/readonly-tuple';
import type { Trackable } from '~/onboarding/types';
import { useSnowplow } from '~/onboarding/hooks/useSnowplow/useSnowplow';
import { isNotNil } from '~/utilities/type-guards';
import { UIProvider } from '@wistia/ui';
import { useLogger } from '../../hooks/useLogger';
import { withErrorBoundary, useErrorBoundary } from '../../hooks/useErrorBoundary';
import { OnboardingContext } from '../../hooks/useOnboardingContext/OnboardingContext';
import { Dots } from '../internal/Dots';
import type { ModalProps } from './Modal';
import type { HeroModalContent, HeroModalContentProps } from './shared/HeroModalContent';
import { ModalCloseButton } from './shared/ModalCloseButton';
import { StyledModalFooter } from './shared/ModalFooter';
import { DEFAULT_HERO_HEIGHT } from './shared/constants';
import type { SharedModalProps } from './types';

const getMediaType = (hasVideo: boolean) => {
  if (hasVideo) {
    return 'video';
  }
  return 'image';
};

export type HeroModalContentComponentType = React.ReactElement<
  HeroModalContentProps,
  typeof HeroModalContent
>;

export type MultiStepModalProps = Pick<ModalProps, 'height'> &
  Pick<SharedModalProps, 'isOpen' | 'onRequestClose'> &
  Trackable & {
    /**
     * Whether or not the steps provided contain a video. Passing `true` will fix the aspect ratio of all of the heroes to `16 / 9` so that all the steps have a consistent size
     */
    hasVideo: boolean;
    /**
     * Content to display at each step. An array length 3-5 of `<HeroModalContent>` components
     */
    steps:
      | ReadonlyTuple<HeroModalContentComponentType, 3>
      | ReadonlyTuple<HeroModalContentComponentType, 4>
      | ReadonlyTuple<HeroModalContentComponentType, 5>;
  };

const HideableButton = styled(Button)<{ $isVisible: boolean }>`
  visibility: ${({ $isVisible }) => ($isVisible ? 'inherit' : 'hidden')};
`;

export const MultiStepModalComponent = ({
  isOpen,
  onRequestClose,
  steps,
  height = DEFAULT_HERO_HEIGHT,
  hasVideo,
  snowplowParams,
}: MultiStepModalProps): JSX.Element => {
  const context = useContext(OnboardingContext);
  const logger = useLogger(context?.getCurrentFeatureId());
  useErrorBoundary((error: Error) => logger.error(error));

  const [currentStep, setCurrentStep] = useState(0);
  const isLastStep = currentStep === steps.length - 1;
  const mediaType = getMediaType(hasVideo);
  const { logEvent } = useSnowplow();

  useEffect(() => {
    if (isOpen) {
      logEvent('multiStepModalView', snowplowParams);
    } else {
      setCurrentStep(0);
    }
  }, [isOpen, logEvent, snowplowParams]);

  const wrappedOnRequestClose = () => {
    logEvent('multiStepModalDismiss', snowplowParams);
    onRequestClose();
  };

  const onClickBack = () => {
    logEvent('multiStepModalClickBack', snowplowParams);
    setCurrentStep(Math.max(currentStep - 1, 0));
  };

  const onClickNext = () => {
    if (isLastStep) {
      wrappedOnRequestClose();
    } else {
      logEvent('multiStepModalClickNext', snowplowParams);
      setCurrentStep(currentStep + 1);
    }
  };

  const currentHeroModalContentWithProps = () => {
    const stepAtCurrentStep = steps[currentStep];
    if (isNotNil(stepAtCurrentStep)) {
      return cloneElement(stepAtCurrentStep, {
        height,
        mediaType,
      });
    }
    return null;
  };

  return (
    <UIProvider>
      <VHSModal
        isOpen={isOpen}
        onRequestClose={wrappedOnRequestClose}
      >
        <ModalCloseButton />
        {currentHeroModalContentWithProps()}
        <StyledModalFooter>
          <Box
            alignItems="center"
            justifyContent="between"
          >
            <HideableButton
              $isVisible={currentStep > 0}
              label="Back"
              iconName="arrow-left"
              onClick={onClickBack}
              variant="secondary"
            />
            <Dots
              total={steps.length}
              current={currentStep}
            />
            <Button
              label={isLastStep ? 'Done' : 'Next'}
              iconName="arrow-right"
              iconPosition="end"
              onClick={onClickNext}
            />
          </Box>
        </StyledModalFooter>
      </VHSModal>
    </UIProvider>
  );
};

export const MultiStepModal = withErrorBoundary(MultiStepModalComponent);
